
import { useState, type FormEvent } from "react";
import { CheckCircle2, Loader2, Send, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/hooks/use-language";
import { submitExamInquiry } from "@/lib/api";
import { createErrorMessage } from "@/lib/error-message";

interface ExamInquiryCardProps {
  className?: string;
}

export function ExamInquiryCard({ className }: ExamInquiryCardProps) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Card
        className={`border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50 dark:border-blue-900 dark:from-blue-950/40 dark:to-purple-950/40 ${className ?? ""}`}
      >
        <CardContent className="flex flex-col items-start gap-4 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white/70 text-primary shadow-sm dark:bg-white/10">
              <Sparkles className="size-5" />
            </div>
            <div>
              <p className="text-base font-semibold">{t("inquiry.title")}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("inquiry.desc")}
              </p>
            </div>
          </div>
          <Button
            size="lg"
            className="w-full cursor-pointer sm:w-auto"
            onClick={() => setOpen(true)}
          >
            <Send className="size-4" />
            {t("inquiry.cta")}
          </Button>
        </CardContent>
      </Card>

      <ExamInquiryDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function ExamInquiryDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useLanguage();
  const [examName, setExamName] = useState("");
  const [source, setSource] = useState("");
  const [motivation, setMotivation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  function reset() {
    setExamName("");
    setSource("");
    setMotivation("");
    setError("");
    setSubmitting(false);
    setDone(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await submitExamInquiry({
        exam_name: examName.trim(),
        source: source.trim() || undefined,
        motivation: motivation.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(createErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          // Slight delay so the closing animation doesn't reveal a reset form.
          setTimeout(reset, 200);
        }
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("inquiry.dialog_title")}</DialogTitle>
          <DialogDescription>{t("inquiry.dialog_desc")}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="size-12 text-emerald-500" />
            <p className="text-sm font-medium">{t("inquiry.success_title")}</p>
            <p className="text-xs text-muted-foreground">
              {t("inquiry.success_desc")}
            </p>
            <Button
              variant="outline"
              className="mt-2 w-full cursor-pointer"
              onClick={() => onOpenChange(false)}
            >
              {t("common.confirm")}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {error && (
              <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="inquiry-exam-name">
                {t("inquiry.field_exam_name")}{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="inquiry-exam-name"
                value={examName}
                onChange={(e) => setExamName(e.target.value)}
                placeholder={t("inquiry.field_exam_name_placeholder")}
                required
                disabled={submitting}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="inquiry-source">
                {t("inquiry.field_source")}
              </Label>
              <Input
                id="inquiry-source"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder={t("inquiry.field_source_placeholder")}
                disabled={submitting}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="inquiry-motivation">
                {t("inquiry.field_motivation")}
              </Label>
              <textarea
                id="inquiry-motivation"
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
                placeholder={t("inquiry.field_motivation_placeholder")}
                disabled={submitting}
                rows={3}
                className="w-full rounded-lg border border-input bg-transparent px-3.5 py-2 text-sm transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30"
              />
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                className="cursor-pointer"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                className="cursor-pointer"
                disabled={submitting || !examName.trim()}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-1 size-4 animate-spin" />
                    {t("common.processing")}
                  </>
                ) : (
                  <>
                    <Send className="size-4" />
                    {t("inquiry.submit")}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
